import { init, type InnspelClient } from '@innspel/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createStorage, Icon, InnspelProvider, useInnspel, webContext, type Draft } from '../lib/index.ts';
import type { PortalKonfig } from './konfig.ts';
import type { Sesjon } from './sesjon.ts';
import { Betaprogram } from './sider/Betaprogram.tsx';
import { Landing } from './sider/Landing.tsx';
import { Mine } from './sider/Mine.tsx';
import { TvOverlevering } from './sider/TvOverlevering.tsx';

export type Rute = { navn: 'landing' } | { navn: 'mine' } | { navn: 'beta' } | { navn: 'tv'; id: string };

/**
 * Sesjonen sett fra portalen. `utlopt` er ikke det samme som `anonym`: hun HAR sendt
 * innspill, vi kan bare ikke hente dem nå. Skillet eier begge tekstene i «Mine innspill»
 * — invitasjonen til å logge inn, og beskjeden om at innloggingen gikk ut.
 */
export type Sesjonstilstand = 'innlogget' | 'utlopt' | 'anonym';

/** Største forsinkelse `setTimeout` kan holde (2^31-1 ms, ~24,8 døgn). Over dette fyrer den straks. */
const MAKS_TIMER = 2_147_483_647;

/**
 * Tokenet lever 15 minutter, og `exp` leses lokalt kun til visning — serveren avgjør.
 * Derfor: et token vi ikke klarer å dekode regnes som innlogget og sendes; blir det
 * avvist, sier serveren fra, og `meldUtlopt` retter tilstanden. Vi låser ingen ute på
 * vår egen halvveis parsing. Motsatt vei er regelen streng («Ukjent tilstand er
 * strengest», `ux-flow-architect`): et `exp` som HAR passert er utløpt her og nå, uten
 * å vente på en 401 som gjør toppen til en løgn i mellomtiden.
 */
export function tilstandAv(sesjon: Sesjon): Sesjonstilstand {
  if (sesjon.token() === null) return 'anonym';
  const om = sesjon.utloperOm();
  return om !== null && om <= 0 ? 'utlopt' : 'innlogget';
}

/** Fire ruter. `/h/<id>` er TV-overleveringen; alt ukjent er landingssiden. */
export function rutFra(pathname: string): Rute {
  const tv = /^\/h\/([^/]+)\/?$/.exec(pathname);
  if (tv?.[1]) return { navn: 'tv', id: decodeURIComponent(tv[1]) };
  if (/^\/mine\/?$/.test(pathname)) return { navn: 'mine' };
  if (/^\/beta\/?$/.test(pathname)) return { navn: 'beta' };
  return { navn: 'landing' };
}

export interface AppProps {
  konfig: PortalKonfig;
  sesjon: Sesjon;
  /** Injiserbar `fetch` — testene gir en fake. */
  fetch?: typeof fetch;
  /** Utkast gjenopprettet etter ny innlogging; åpner dialogen med teksten. */
  utkast?: Draft | null;
  navigator?: { userAgent?: string; language?: string };
  /** Redirecten til leietakeren. Injiserbar — jsdom lar ikke `location.assign` spionere. */
  assign?: (url: string) => void;
}

/**
 * Portalen — skallet fra portal.jsx rundt `@innspel/web` sine komponenter.
 * Klienten bygges fra sesjonen: med token er den identifisert, uten er den
 * anonym (`identityProvider: null`), og den byttes når innloggingen endrer seg.
 * Provideren får da riktig `isAnonymous` for kvitteringen og «Mine innspill».
 */
export function App({ konfig, sesjon, fetch: hent, utkast, navigator: nav, assign = (url) => location.assign(url) }: AppProps) {
  const [sti, setSti] = useState(() => location.pathname);
  const [tilstand, setTilstand] = useState<Sesjonstilstand>(() => tilstandAv(sesjon));
  const innlogget = tilstand === 'innlogget';

  useEffect(() => {
    const onPop = () => setSti(location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Sesjonen er død: tokenet er et bærertoken uten verdi og blir ikke liggende i
  // `sessionStorage`. Dekker både et token som alt var utløpt ved innlasting og ett
  // som døde mens siden sto åpen.
  useEffect(() => {
    if (tilstand === 'utlopt') sesjon.loggUt();
  }, [tilstand, sesjon]);

  // Tokenet lever 15 minutter. Går det ut mens hun leser lista, skal toppen og fanene
  // si fra der og da — ikke først ved neste kall som feiler.
  //
  // Taket er ikke pynt: `setTimeout` klamper alt over ~24,8 døgn til å fyre STRAKS. Et
  // token med `exp` lenger fram enn det — en feilkonfigurert leietaker, ikke en normal
  // sesjon — ville da logget ut en innsender med gyldig token på første render. Er
  // utløpet så langt unna at det ikke kan times, er det heller ikke vårt å gjette på:
  // serveren avgjør, som ellers.
  useEffect(() => {
    if (tilstand !== 'innlogget') return;
    const om = sesjon.utloperOm();
    if (om === null || om > MAKS_TIMER) return;
    const timer = setTimeout(() => setTilstand('utlopt'), Math.max(om, 0));
    return () => clearTimeout(timer);
  }, [tilstand, sesjon]);

  useEffect(() => {
    document.title = `Innspill til ${konfig.tenantName}`;
    document.documentElement.setAttribute('data-innspel-theme', konfig.theme);
    document.documentElement.lang = 'nb';
  }, [konfig.tenantName, konfig.theme]);

  const naviger = useCallback((til: string) => {
    history.pushState(null, '', til);
    setSti(til);
  }, []);

  const loggUt = useCallback(() => {
    sesjon.loggUt();
    setTilstand('anonym');
  }, [sesjon]);

  // «Mine innspill» fikk 401 `feedback_token_expired`/`_required`. Den beskjeden eide
  // siden før, i lokal state — så banneret kom, mens toppen fortsatt sa «Logg ut» og
  // fanen sto igjen (produkteiers gjennomgang 2026-09-16).
  const meldUtlopt = useCallback(() => setTilstand('utlopt'), []);

  const client = useMemo<InnspelClient>(
    () =>
      init({
        baseUrl: konfig.baseUrl,
        tenantKey: konfig.tenantKey,
        // Tokenet leses friskt per kall og lagres aldri av kjernen (K20). Er det
        // utløpt, sier serveren det, og dialogen beholder utkastet.
        identityProvider: innlogget ? () => sesjon.token() : null,
        storage: createStorage(),
        context: webContext({}, nav),
        textMaxLength: konfig.textMaxLength,
        minTextLength: konfig.minTextLength,
        screenshotsEnabled: konfig.screenshotsEnabled,
        ...(hent ? { fetch: hent } : {}),
      }),
    [konfig, sesjon, innlogget, hent, nav],
  );

  const tilInnlogging = useCallback(
    (retur = location.pathname) => {
      if (!konfig.loginRedirectUrl) return;
      assign(sesjon.innloggingsUrl(konfig.loginRedirectUrl, new URL(retur, location.origin).toString()));
    },
    [konfig.loginRedirectUrl, sesjon, assign],
  );

  // Utløpt token mens hun skrev: utkastet lagres, og hun sendes til ny innlogging.
  // Tilbake på /ny åpnes dialogen med teksten (main.tsx henter utkastet).
  const onIdentityExpired = useCallback(
    (u: Draft) => {
      sesjon.lagreUtkast(u);
      tilInnlogging('/ny');
    },
    [sesjon, tilInnlogging],
  );

  const rute = rutFra(sti);

  return (
    <InnspelProvider
      client={client}
      tenantName={konfig.tenantName}
      colorScheme={konfig.theme}
      {...(konfig.imageStepNote ? { imageStepNote: konfig.imageStepNote } : {})}
      // Portalen er ikke stedet feilen er: et bilde av DENNE siden viser
      // landingssiden, ikke appen innsenderen kom fra (C9). «Last opp bilde»
      // finnes fortsatt når leietakeren har skjermbilde på.
      screenshot={false}
      injectStyles={false}
      {...(konfig.loginRedirectUrl ? { onIdentityExpired } : {})}
      onSendAnonymously={loggUt}
    >
      <Skall konfig={konfig} innlogget={innlogget} rute={rute} naviger={naviger} onLoggInn={() => tilInnlogging()} onLoggUt={loggUt} utkast={utkast ?? null}>
        {rute.navn === 'landing' ? <Landing konfig={konfig} innlogget={innlogget} onLoggInn={() => tilInnlogging()} /> : null}
        {rute.navn === 'mine' ? <Mine client={client} konfig={konfig} tilstand={tilstand} onLoggInn={() => tilInnlogging('/mine')} onUtlopt={meldUtlopt} /> : null}
        {rute.navn === 'beta' ? <Betaprogram client={client} konfig={konfig} innlogget={innlogget} invitasjonstoken={sesjon.invitasjonstoken()} onAkseptert={() => sesjon.glemInvitasjon()} /> : null}
        {rute.navn === 'tv' ? <TvOverlevering id={rute.id} konfig={konfig} onLoggInn={() => tilInnlogging()} {...(hent ? { fetch: hent } : {})} /> : null}
      </Skall>
    </InnspelProvider>
  );
}

function Skall({
  konfig,
  innlogget,
  rute,
  naviger,
  onLoggInn,
  onLoggUt,
  utkast,
  children,
}: {
  konfig: PortalKonfig;
  innlogget: boolean;
  rute: Rute;
  naviger: (til: string) => void;
  onLoggInn: () => void;
  onLoggUt: () => void;
  utkast: Draft | null;
  children: React.ReactNode;
}) {
  const { open } = useInnspel();

  // Gjenopprettet utkast etter ny innlogging: rett i feltet, med teksten.
  useEffect(() => {
    if (utkast) void open({ draft: utkast });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lenke = (til: string, e: React.MouseEvent) => {
    e.preventDefault();
    naviger(til);
  };
  const faner: Array<[string, string, string]> = innlogget ? [['list-outline', 'Mine innspill', '/mine'], ...(konfig.beta ? ([['flask-outline', 'Betaprogram', '/beta']] as Array<[string, string, string]>) : [])] : [];

  return (
    <div className="innspel-rot portal">
      <a href="#innhold" className="portal-hopp">
        Hopp til innhold
      </a>
      <header className="portal-hode">
        <a href="/" className="portal-merke" aria-label={`${konfig.tenantName} — til forsiden`} onClick={(e) => lenke('/', e)}>
          <span className="portal-merke-flate" aria-hidden="true" />
          <span className="portal-merke-navn">
            <strong>{konfig.tenantName}</strong>
            <span>Innspel — alle får svar</span>
          </span>
        </a>
        {innlogget ? (
          // DD-64: én handling, sagt som handling. At man er innlogget ses av at «Mine innspill»
          // finnes. Ingen visningsform av pseudonymet: ingen kan slå den opp — panelet og appen
          // viser den ikke — så den var en intern kode på skjermen (DD-50).
          <button type="button" className="portal-innlogging" data-testid="logg-ut" onClick={onLoggUt}>
            <Icon navn="exit-outline" />
            Logg ut
          </button>
        ) : konfig.loginRedirectUrl ? (
          <button type="button" className="portal-innlogging" data-testid="logg-inn" onClick={onLoggInn}>
            <Icon navn="key-outline" />
            Logg inn
          </button>
        ) : null}
      </header>
      {faner.length ? (
        <nav aria-label="Portal" className="portal-faner">
          {faner.map(([ikon, navn, til]) => {
            const aktiv = (til === '/mine' && rute.navn === 'mine') || (til === '/beta' && rute.navn === 'beta');
            return (
              <a key={til} href={til} className="portal-fane" aria-current={aktiv ? 'page' : undefined} onClick={(e) => lenke(til, e)}>
                <Icon navn={ikon} />
                {navn}
              </a>
            );
          })}
        </nav>
      ) : null}
      <main id="innhold" tabIndex={-1} className="portal-innhold">
        {children}
      </main>
      <footer className="portal-fot">
        Drives av Innspel for {konfig.tenantName}. Personvern:{' '}
        <a href={konfig.privacyUrl} rel="noreferrer">
          {vertsnavn(konfig.privacyUrl)} <Icon navn="open-outline" />
        </a>
      </footer>
    </div>
  );
}

function vertsnavn(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
